// Hover help: the acuity experiment's settings.

import { AcuityStaircases, PriorPicture, PsychometricPicture, SimNumbers, acuityOf } from "./pictures";
import type { HelpContext, HelpEntry } from "./types";

const m = (body: string) => `<math display="block">${body}</math>`;
const mi = (s: string) => `<mi>${s}</mi>`;
const mn = (s: string | number) => `<mn>${s}</mn>`;
const mo = (s: string) => `<mo>${s}</mo>`;
const frac = (a: string, b: string) => `<mfrac><mrow>${a}</mrow><mrow>${b}</mrow></mfrac>`;

const SIM_SOURCE = "Numbers under the picture: simulated tests of a patient at 0.1 logMAR with this app's staircase engine "
  + "(logistic observer, slope 15 per logMAR, 25% guessing, 2% lapses).";

const sc = (highlight: Parameters<typeof AcuityStaircases>[0]["highlight"]) => (ctx: HelpContext) => (
  <>
    <AcuityStaircases ctx={ctx} highlight={highlight} />
    <SimNumbers ctx={ctx} kind="acuity" />
  </>
);

const psy = (mark: "beta" | "gamma" | "delta") => (ctx: HelpContext) => {
  const a = acuityOf(ctx);
  return <PsychometricPicture beta={a.quest_beta} gamma={a.quest_gamma} delta={a.quest_delta} mark={mark} unit="logMAR" />;
};

export const ACUITY_HELP: Record<string, HelpEntry> = {
  "acuity.method": {
    title: "Acuity method",
    what: "How the smallest letter the patient can read is found. Two staircases run together, taking turns at random: one "
      + "starts with large letters, one with small; the result is their mean.\n\n"
      + "QUEST (default): Bayesian; after each answer it shows the letter size it currently thinks is the threshold. Starts "
      + "at the largest letter and steps down until the first error.\n\nWeighted (Kaernbach), Levitt and simple up/down: "
      + "a smaller letter after right answers, a larger one after wrong ones; the threshold is the mean of the last reversals.",
    math: m(`${mi("logMAR")}${mo("=")}<msub>${mi("log")}${mn(10)}</msub><mo>(</mo>${mi("MAR")}<mo>)</mo>`),
    mathNote: "MAR: the stroke width in arcminutes. 0.0 logMAR = 20/20 (6/6); 0.3 = 20/40; 1.0 = 20/200.",
    picture: sc("threshold"),
    scenarios: [
      { scenario: "Default", value: "QUEST, 20 trials each, 10% checks", why: "about 44 trials per eye; SD about 0.04" },
      { scenario: "Patients who guess a lot", value: "Levitt, n = 3", why: "needs runs of right answers to go smaller" },
    ],
    source: SIM_SOURCE,
  },
  "acuity.min_logmar": {
    title: "Smallest letter",
    what: "The smallest letter a staircase may show (logMAR). The screen also limits it: a stroke cannot be thinner than one "
      + "pixel, so on a given screen and distance there is a floor (the session records it).",
    math: m(`${mi("stroke px")}${mo("=")}${mi("px per degree")}${mo("×")}${frac("<msup><mn>10</mn><mi>logMAR</mi></msup>", mn(60))}`),
    range: "−0.3 to 0.5; default −0.3.",
  },
  "acuity.max_logmar": {
    title: "Largest letter",
    what: "The largest letter (logMAR); attention checks use it. Also limited by the screen's height.",
    range: "0.5–1.6; default 1.0.",
    scenarios: [
      { scenario: "Typical patients", value: "1.0", why: "20/200 at the top" },
      { scenario: "Low vision", value: "1.3–1.6", why: "room above poor acuities (screen height permitting)" },
    ],
  },
  "acuity.quest_trials": {
    title: "QUEST: trials per staircase",
    what: "Trials for each of the two QUEST staircases. Total ≈ 2 × this (plus attention checks).",
    range: "8–60; default 20.",
    picture: sc("count"),
  },
  "acuity.quest_prior_mean": {
    title: "QUEST: prior guess",
    what: "Where QUEST expects the acuity before any answers (logMAR). With a broad prior it matters little.",
    picture: (ctx) => { const a = acuityOf(ctx); return <PriorPicture mean={a.quest_prior_mean} sd={a.quest_prior_sd} lo={-0.3} hi={1.3} unit="logMAR" />; },
    range: "default 0.3.",
  },
  "acuity.quest_prior_sd": {
    title: "QUEST: prior uncertainty",
    what: "How unsure QUEST is about the acuity before any answers (SD, logMAR).",
    picture: (ctx) => { const a = acuityOf(ctx); return <PriorPicture mean={a.quest_prior_mean} sd={a.quest_prior_sd} lo={-0.3} hi={1.3} unit="logMAR" />; },
    range: "default 0.5.",
  },
  "acuity.quest_beta": {
    title: "QUEST: slope",
    what: "How sharply the chance of a right answer rises around the acuity threshold (per logMAR).",
    math: m(`${mi("ψ")}<mo>(</mo>${mi("x")}<mo>)</mo>${mo("=")}${mi("γ")}${mo("+")}<mfrac><mrow>${mn(1)}${mo("−")}${mi("γ")}${mo("−")}${mi("δ")}</mrow><mrow>${mn(1)}${mo("+")}<msup>${mi("e")}<mrow>${mo("−")}${mi("β")}<mo>(</mo>${mi("x")}${mo("−")}${mi("T")}<mo>)</mo></mrow></msup></mrow></mfrac>`),
    mathNote: "x: the letter size in logMAR; T: the acuity threshold.",
    picture: psy("beta"),
    range: "default 15.",
  },
  "acuity.quest_gamma": { title: "QUEST: guess rate", what: "The chance of a right answer by guessing: 0.25 for a 4-way E.", picture: psy("gamma") },
  "acuity.quest_delta": { title: "QUEST: lapse rate", what: "The chance of a wrong answer to an easily visible letter.", picture: psy("delta"), range: "default 0.04." },
  "acuity.quest_grain": { title: "QUEST: grid step", what: "Resolution of QUEST's grid of possible acuities (logMAR).", range: "default 0.01." },
  "acuity.quest_initial_easy": {
    title: "QUEST: easy letters first",
    what: "Trials at the largest letter before the descent starts: a gentle start that shows the patient what to do.",
    range: "0–5; default 1.",
    picture: sc("start"),
  },
  "acuity.quest_descent_step": {
    title: "QUEST: descent step",
    what: "After each right answer the letters shrink by this much, until the first error; then QUEST takes over. A quick "
      + "way down from the large letters.",
    range: "0.1–0.5 logMAR; default 0.3.",
    picture: sc("step"),
  },
  "acuity.start_low": { title: "Start of the small-letter staircase", what: "Where the staircase that begins with small letters starts (logMAR).", range: "default 0.0.", picture: sc("start") },
  "acuity.start_high": { title: "Start of the large-letter staircase", what: "Where the staircase that begins with large letters starts (logMAR).", range: "default 1.0.", picture: sc("start") },
  "acuity.step": { title: "First step", what: "How far an up/down staircase moves at first (logMAR); multiplied by the step factor at the 2nd reversal.", range: "0.05–0.4; default 0.2.", picture: sc("step") },
  "acuity.step_factor": {
    title: "Step factor",
    what: "At the 2nd reversal the step is multiplied by this: big steps to get near the threshold, small ones to measure it.",
    math: m(`${mi("Δ")}<mo>′</mo>${mo("=")}${mi("f")}${mo("·")}${mi("Δ")}`),
    range: "0.25–1; default 0.5.",
  },
  "acuity.levitt_n_down": {
    title: "Right answers in a row before a smaller letter (Levitt)",
    what: "The Levitt staircase shows a smaller letter only after this many right answers in a row.",
    math: m(`${mi("p")}${mo("=")}<msup>${mn("0.5")}<mrow>${mn(1)}${mo("/")}${mi("n")}</mrow></msup>`),
    mathNote: "2: 70.7%, 3: 79.4%, 4: 84.1% right at convergence.",
    picture: sc("reversal"),
  },
  "acuity.weighted_target": {
    title: "Target share right (Kaernbach)",
    what: "The share of right answers the weighted staircase settles at.",
    math: m(`<msub>${mi("Δ")}<mtext>up</mtext></msub>${mo("=")}<msub>${mi("Δ")}<mtext>down</mtext></msub>${mo("·")}<mfrac>${mi("p")}<mrow>${mn(1)}${mo("−")}${mi("p")}</mrow></mfrac>`),
    range: "0.5–0.95; default 0.75.",
  },
  "acuity.max_reversals": { title: "Reversals to stop", what: "Each up/down staircase stops after this many reversals.", range: "4–20; default 10.", picture: sc("stop") },
  "acuity.reversals_to_average": {
    title: "Reversals averaged",
    what: "An up/down staircase's threshold is the mean of its last this-many reversals.",
    range: "2–12; default 6.",
    picture: sc("reversal"),
  },
  "acuity.catch_rate": {
    title: "Attention checks",
    what: "A share of trials at the largest letter, fed to no staircase. A patient paying attention gets them right.",
    range: "0–30%; default 10%.",
    picture: sc("catch"),
    scenarios: [{ scenario: "Unsupervised home use", value: "10%", why: "about 4 checks in a 40-trial test" }],
  },
  "acuity.catch_min_pct": { title: "Attention checks right at least", what: "Fewer right than this: the result is flagged or made invalid.", range: "default 80%." },
  "acuity.catch_action": { title: "If too few attention checks are right", what: "Flag: counts, with a warning. Invalid: does not count; the test is repeated." },
};
